import {
  Amount,
  AuthenticatedAppraisedEmailAddress,
  PaymentFrequency,
  PaymentSourceKind,
  PostalAddress,
  FormSourceRef,
  IBANValidationResult,
} from "@evergiving/common/dist/es2015/pledge";
import { PledgePaymentAuthorizationState } from "@/lib/misc/pledgePaymentAuthorization";
import {
  CustomInputTag,
  EmailAddressFieldConfig,
  IBANFieldConfig,
} from "@evergiving/common/dist/es2015/form";

import { ThunkAction } from "redux-thunk";

import {
  errorsForEmailAddress,
  errorsForIBAN,
  validationContextSelector,
} from "@/lib/misc/validations";

import {
  SET_PRESET_AMOUNT,
  SET_CUSTOM_CURRENCY,
  SET_PAYMENT_FREQUENCY,
  SET_PAYMENT_SOURCE_KIND,
  SET_PAYMENT_DAY,
  SET_POSTAL_ADDRESS,
  SET_COUNTRY_CODE,
  SET_BELFIORE_CODE,
  SET_START_DATE,
  SET_FORM_VALUE,
  SET_CUSTOM_INPUT_FORM_VALUE,
  SET_BOOLEAN_FORM_VALUE,
  SET_PARTIAL_STATE,
  RESET_FORM_ERRORS,
  SET_FORM_ERRORS,
  SET_FORM_SOURCE_REF,
  SET_FORM_PLEDGE_PAYMENT_AUTHORIZATION,
  SET_EMAIL_ADDRESS,
  SET_IBAN,
  SET_CC_NAME,
  RootAction,
  VersionedStringFormState,
  BooleanFormState,
  RootState,
  FormState,
} from "./types";
import { NetworkError } from "./network-error";

export function setPresetAmount(
  amount: Amount | null,
  locale: string
): RootAction {
  return {
    type: SET_PRESET_AMOUNT,
    payload: { amount, locale },
  };
}

export function setCustomCurrency(currency: string): RootAction {
  return {
    type: SET_CUSTOM_CURRENCY,
    payload: currency,
  };
}

export function setPaymentFrequency(frequency: PaymentFrequency): RootAction {
  return {
    type: SET_PAYMENT_FREQUENCY,
    payload: frequency,
  };
}

export function setPaymentSourceKind(
  paymentSourceKind: PaymentSourceKind
): RootAction {
  return {
    type: SET_PAYMENT_SOURCE_KIND,
    payload: paymentSourceKind,
  };
}

export function setPostalAddress(postalAddress?: PostalAddress): RootAction {
  return {
    type: SET_POSTAL_ADDRESS,
    payload: postalAddress,
  };
}

export function setCountryCode(countryCode: string): RootAction {
  return {
    type: SET_COUNTRY_CODE,
    payload: countryCode,
  };
}

export function setBelfioreCode(belfioreCode: string): RootAction {
  return {
    type: SET_BELFIORE_CODE,
    payload: belfioreCode,
  };
}

export function setStartDate(startDate: string): RootAction {
  return {
    type: SET_START_DATE,
    payload: startDate,
  };
}

export function setPaymentDay(day: number): RootAction {
  return {
    type: SET_PAYMENT_DAY,
    payload: day,
  };
}

export function setFormValue(
  attribute: keyof VersionedStringFormState,
  value: string | undefined,
  anyErrors: boolean
): RootAction {
  return {
    type: SET_FORM_VALUE,
    payload: { attribute, value, anyErrors },
  };
}

export function setCCName(
  attribute: "cardHolder" | "directDebitAccountHolder",
  value: string | undefined,
  anyErrors: boolean
): RootAction {
  return {
    type: SET_CC_NAME,
    payload: { attribute, value, anyErrors },
  };
}

export function setCustomInputFormValue(
  customInputTag: CustomInputTag,
  value: string
): RootAction {
  return {
    type: SET_CUSTOM_INPUT_FORM_VALUE,
    payload: { customInputTag, value },
  };
}

export function resetFormErrors(
  attribute: keyof VersionedStringFormState
): RootAction {
  return {
    type: RESET_FORM_ERRORS,
    payload: attribute,
  };
}

export function setFormErrors(
  errors: Partial<{ [key in keyof FormState]: boolean }>
): RootAction {
  return {
    type: SET_FORM_ERRORS,
    payload: errors,
  };
}

export function setBooleanFormValue(
  attribute: keyof BooleanFormState,
  value: boolean,
  anyErrors: boolean
): RootAction {
  return {
    type: SET_BOOLEAN_FORM_VALUE,
    payload: { attribute, value, anyErrors },
  };
}

export function setPartialState(partialState: Partial<RootState>): RootAction {
  return {
    type: SET_PARTIAL_STATE,
    payload: partialState,
  };
}

export function setFormSourceRef(formSourceRef: FormSourceRef): RootAction {
  return {
    type: SET_FORM_SOURCE_REF,
    payload: formSourceRef,
  };
}

export function setFormPledgePaymentAuthorization(
  formPledgePaymentAuthorization: PledgePaymentAuthorizationState
): RootAction {
  return {
    type: SET_FORM_PLEDGE_PAYMENT_AUTHORIZATION,
    payload: formPledgePaymentAuthorization,
  };
}

const pendingAppraisals = new Map<
  string,
  Promise<AuthenticatedAppraisedEmailAddress | null>
>();
const resolvedAppraisals = new Map<
  string,
  AuthenticatedAppraisedEmailAddress
>();

async function appraiseEmail({
  appraisalEndpointUrl,
  emailAddress,
}: {
  appraisalEndpointUrl: string;
  emailAddress: string;
}): Promise<AuthenticatedAppraisedEmailAddress | null> {
  const url = new URL(appraisalEndpointUrl);
  url.search = new URLSearchParams({
    email: emailAddress,
  }).toString();

  try {
    const response = await fetch(url.toString());

    if (response.ok) {
      return (await response.json()) as AuthenticatedAppraisedEmailAddress;
    }

    return null;
  } catch (error) {
    if (error instanceof TypeError) {
      // Let's get more precise about the error
      throw new NetworkError(error);
    } else {
      console.warn(error);
    }

    return null;
  }
}

export const setEmailAddress =
  (
    config: EmailAddressFieldConfig,
    emailAddress: string | undefined
  ): ThunkAction<void, RootState, unknown, RootAction> =>
  async (dispatch, getRootState) => {
    const validationContext = validationContextSelector(getRootState());

    const anyErrors =
      errorsForEmailAddress(config, emailAddress, undefined, validationContext)
        .length > 0;

    // Even without an appraisal, the email is rejected
    if (anyErrors) {
      dispatch({
        type: SET_EMAIL_ADDRESS,
        payload: {
          emailAddress,
          appraisal: undefined,
          anyErrors:
            errorsForEmailAddress(
              config,
              emailAddress,
              undefined,
              validationContext
            ).length > 0,
        },
      });
      return;
    }

    // Workaround to make sure an email address that's been already validated locally is marked as valid before continuing with async appraisal
    dispatch({
      type: SET_EMAIL_ADDRESS,
      payload: {
        emailAddress,
        appraisal: undefined,
        anyErrors: false,
      },
    });

    // Absent email address is valid - we're done
    if (!emailAddress) {
      return;
    }

    // Appraisal already in progress, or not possible
    if (pendingAppraisals.has(emailAddress) || !("validateAddress" in config)) {
      return;
    }

    // Email address appraised before
    const resolvedAppraisal = resolvedAppraisals.get(emailAddress);
    if (resolvedAppraisal) {
      dispatch({
        type: SET_EMAIL_ADDRESS,
        payload: {
          emailAddress,
          appraisal: resolvedAppraisal,
          anyErrors:
            errorsForEmailAddress(
              config,
              emailAddress,
              resolvedAppraisal.appraisedEmailAddress.appraisal,
              validationContext
            ).length > 0,
        },
      });
      return;
    }

    // Case 4: appraisal necessary

    const { appraisalEndpointUrl } = config.validateAddress;
    const appraisalPromise = appraiseEmail({
      appraisalEndpointUrl,
      emailAddress,
    });
    pendingAppraisals.set(emailAddress, appraisalPromise);
    try {
      const appraisal = await appraisalPromise;

      if (appraisal) {
        resolvedAppraisals.set(emailAddress, appraisal);

        dispatch({
          type: SET_EMAIL_ADDRESS,
          payload: {
            emailAddress,
            appraisal,
            anyErrors:
              errorsForEmailAddress(
                config,
                emailAddress,
                appraisal.appraisedEmailAddress.appraisal,
                getRootState()
              ).length > 0,
          },
        });
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        console.warn(
          "Network error while appraising email address",
          error.cause
        );

        dispatch({
          type: SET_EMAIL_ADDRESS,
          payload: {
            emailAddress,
            appraisal: undefined,
            anyErrors: false,
          },
        });
      }
    } finally {
      pendingAppraisals.delete(emailAddress);
    }
  };

const pendingIbanAppraisals = new Map<
  string,
  Promise<IBANValidationResult | null>
>();
const resolvedIbanAppraisals = new Map<string, IBANValidationResult>();

async function appraiseIBAN({
  appraisalEndpointUrl,
  iban,
}: {
  appraisalEndpointUrl: string;
  iban: string;
}): Promise<IBANValidationResult | null> {
  const url = new URL(appraisalEndpointUrl);
  url.search = new URLSearchParams({
    query: iban,
  }).toString();

  try {
    const response = await fetch(url.toString());

    if (response.ok) {
      return (await response.json()) as IBANValidationResult;
    }

    return null;
  } catch (error) {
    if (error instanceof TypeError) {
      // Let's get more precise about the error
      throw new NetworkError(error);
    } else {
      console.warn(error);
    }

    return null;
  }
}

export const setIBAN =
  (
    config: IBANFieldConfig,
    iban: string | undefined
  ): ThunkAction<void, RootState, unknown, RootAction> =>
  async (dispatch, getRootState) => {
    const validationContext = validationContextSelector(getRootState());

    const anyErrors =
      errorsForIBAN(config, iban, undefined, validationContext).length > 0;

    // Even without an appraisal, the iban is rejected
    if (anyErrors) {
      dispatch({
        type: SET_IBAN,
        payload: {
          iban,
          appraisal: undefined,
          anyErrors:
            errorsForIBAN(config, iban, undefined, validationContext).length >
            0,
        },
      });
      return;
    }

    // Absent email address is valid - we're done
    if (!iban) {
      return;
    }

    // Appraisal already in progress, or not possible
    if (
      pendingIbanAppraisals.has(iban) ||
      !("appraisalEndpointUrl" in config)
    ) {
      return;
    }

    // Email address appraised before
    const resolvedIbanAppraisal = resolvedIbanAppraisals.get(iban);
    if (resolvedIbanAppraisal) {
      dispatch({
        type: SET_IBAN,
        payload: {
          iban,
          appraisal: resolvedIbanAppraisal,
          anyErrors:
            errorsForIBAN(
              config,
              iban,
              resolvedIbanAppraisal,
              validationContext
            ).length > 0,
        },
      });
      return;
    }

    // Case 4: appraisal necessary

    dispatch({
      type: SET_IBAN,
      payload: {
        iban,
        appraisal: undefined,
        anyErrors:
          errorsForIBAN(config, iban, undefined, getRootState()).length > 0,
      },
    });

    const { appraisalEndpointUrl } = config;
    const appraisalPromise = appraiseIBAN({
      appraisalEndpointUrl,
      iban,
    });
    pendingIbanAppraisals.set(iban, appraisalPromise);
    try {
      const appraisal = await appraisalPromise;

      if (appraisal) {
        resolvedIbanAppraisals.set(iban, appraisal);

        dispatch({
          type: SET_IBAN,
          payload: {
            iban,
            appraisal,
            anyErrors:
              errorsForIBAN(config, iban, appraisal, getRootState()).length > 0,
          },
        });
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        console.warn("Network error while appraising IBAN", error.cause);

        dispatch({
          type: SET_IBAN,
          payload: {
            iban,
            appraisal: undefined,
            anyErrors: false,
          },
        });
      }
    } finally {
      pendingIbanAppraisals.delete(iban);
    }
  };
